<script>
  import { onDestroy, tick } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import annotationPlugin from 'chartjs-plugin-annotation';
  import { theme } from '$stores/themeStore';
  import { get } from 'svelte/store';
  import { toDate, formatInTimeZone } from 'date-fns-tz';
  import { addDays } from 'date-fns';
  import { studyProperties } from '$stores/configStore';
  import { formatToLocalDate } from '$utils/dateUtils.js';
  // SPTL-492: legacy imported this from backend JS (services/jobs/...).
  // NG backend is Spring Boot — no equivalent JS file. Inline the constant
  // here. If the backend rule5 lookback changes, mirror it here.
  const RULE5_FIRST_CGM_LOOKBACK_DAYS_BEFORE_S = 7;

  /** Daily compliance rows: { day: 'yyyy-MM-dd', totalCount } */
  export let data = [];
  /** Subject activity (visit): planned, started, windowMin, windowMax, name */
  export let visit = null;
  export let timezone = 'UTC';
  /** 'planned' | 'started' | 'visitDate' — non-planned modes fall back to planned when the chosen date is missing */
  export let anchorMode = 'planned';
  export let participantAlertJobConfig = null;
  export let siteAlertJobConfig = null;
  export let showAlertSchedule = false;

  const PARTICIPANT_CGM_ALERT_JOB_ID = 'subject-activity-cgm-compliance';
  const SITE_CGM_ALERT_JOB_ID = 'subject-activity-cgm-site-compliance';
  const PARTICIPANT_ALERT_DEFAULTS = {
    rule2WeekOffsetDays: 7,
    rule3MinSufficientDays: 10,
    rule4MinSufficientDays: 20,
    rule4StartDaysBeforePlanned: 6,
    sensorLifetimeReminderDays: 10,
    expectedReadingsPerDay: 288,
    sufficientReadingFraction: 0.8,
    sufficientDayMaxGapHours: 2,
  };
  const SITE_ALERT_DEFAULTS = {
    rule2WeekOffsetDays: 7,
    minimumUsableDaysForAlert: 10,
    requiredAnyDataDays: 20,
    expectedReadingsPerDay: 288,
    sufficientReadingFraction: 0.8,
    sufficientDayMaxGapHours: 2,
  };

  let canvas;
  let chart;
  let pluginsRegistered = false;

  /** Stable key so switching visits always rebuilds the chart with the new window */
  $: visitKey = visit?._id ?? '';

  function ensurePlugins() {
    if (pluginsRegistered) return;
    Chart.register(...registerables, annotationPlugin);
    pluginsRegistered = true;
  }

  $: expectedPerDay = Math.round(
    1440 / ($studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5)
  );
  $: thresholdPct = $studyProperties?.glucoseParameters?.cgmCompliance?.complianceThreshold ?? 80;
  $: targetValue = Math.round(expectedPerDay * (thresholdPct / 100));

  /** Returns instant to center the window on, and how we resolved it for labels */
  function resolveCenterMeta(v, mode) {
    const empty = {
      instant: null,
      resolved: 'none',
      usedStarted: false,
      usedVisitDate: false,
      fellBackHint: null,
    };
    if (!v) return empty;

    if (mode === 'planned') {
      if (v.planned != null && String(v.planned).trim() !== '') {
        const d = new Date(v.planned);
        if (!isNaN(d.getTime())) {
          return { instant: d, resolved: 'planned', usedStarted: false, usedVisitDate: false, fellBackHint: null };
        }
      }
      return empty;
    }

    if (mode === 'started') {
      if (v.started != null && String(v.started).trim() !== '') {
        const d = new Date(v.started);
        if (!isNaN(d.getTime())) {
          return { instant: d, resolved: 'started', usedStarted: true, usedVisitDate: false, fellBackHint: null };
        }
      }
      if (v.planned != null && String(v.planned).trim() !== '') {
        const d = new Date(v.planned);
        if (!isNaN(d.getTime())) {
          return {
            instant: d,
            resolved: 'planned',
            usedStarted: false,
            usedVisitDate: false,
            fellBackHint: 'started',
          };
        }
      }
      return empty;
    }

    if (mode === 'visitDate') {
      if (v.visitDate != null && String(v.visitDate).trim() !== '') {
        const d = new Date(v.visitDate);
        if (!isNaN(d.getTime())) {
          return {
            instant: d,
            resolved: 'visitDate',
            usedStarted: false,
            usedVisitDate: true,
            fellBackHint: null,
          };
        }
      }
      if (v.planned != null && String(v.planned).trim() !== '') {
        const d = new Date(v.planned);
        if (!isNaN(d.getTime())) {
          return {
            instant: d,
            resolved: 'planned',
            usedStarted: false,
            usedVisitDate: false,
            fellBackHint: 'visitDate',
          };
        }
      }
      return empty;
    }

    return empty;
  }

  function buildDayRange(centerInstant, windowMin, windowMax, tz) {
    if (centerInstant == null || isNaN(new Date(centerInstant).getTime())) {
      return { dayStrings: [], centerIndex: 0 };
    }
    const wm = Number(windowMin) || 0;
    const wM = Number(windowMax) || 0;
    const centerYmd = formatInTimeZone(new Date(centerInstant), tz, 'yyyy-MM-dd');
    const anchor = toDate(`${centerYmd}T12:00:00`, { timeZone: tz });
    if (isNaN(anchor.getTime())) {
      return { dayStrings: [], centerIndex: 0 };
    }
    const dayStrings = [];
    for (let k = -wm; k <= wM; k++) {
      dayStrings.push(formatInTimeZone(addDays(anchor, k), tz, 'yyyy-MM-dd'));
    }
    return { dayStrings, centerIndex: wm };
  }

  function normalizeParameters(config) {
    const raw = config?.parameters;
    if (!raw) return {};
    if (raw instanceof Map) return Object.fromEntries(raw);
    if (Array.isArray(raw)) return Object.fromEntries(raw);
    if (typeof raw === 'object') return raw;
    return {};
  }

  function numericParam(config, key, fallback) {
    const params = normalizeParameters(config);
    const value = params[key];
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  function localCalendarPlusDays(ymd, deltaDays, tz) {
    try {
      const anchor = toDate(`${ymd}T12:00:00`, { timeZone: tz });
      return formatInTimeZone(addDays(anchor, deltaDays), tz, 'yyyy-MM-dd');
    } catch {
      return ymd;
    }
  }

  function compareYmd(a, b) {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  }

  function isYmdInRange(ymd, start, end) {
    return compareYmd(ymd, start) >= 0 && compareYmd(ymd, end) <= 0;
  }

  function resolvePlannedYmd(v, tz) {
    if (!v?.planned) return null;
    const planned = new Date(v.planned);
    if (isNaN(planned.getTime())) return null;
    return formatInTimeZone(planned, tz, 'yyyy-MM-dd');
  }

  function getPlannedAlertWindow(v, tz) {
    const P = resolvePlannedYmd(v, tz);
    if (!P) return null;
    const windowMin = Number(v.windowMin) || 0;
    const windowMax = Number(v.windowMax) || 0;
    return {
      P,
      S: localCalendarPlusDays(P, -windowMin, tz),
      E: localCalendarPlusDays(P, windowMax, tz),
    };
  }

  $: centerMeta = visit
    ? resolveCenterMeta(visit, anchorMode)
    : {
        instant: null,
        resolved: 'none',
        usedStarted: false,
        usedVisitDate: false,
        fellBackHint: null,
      };
  $: range =
    visit && centerMeta.instant
      ? buildDayRange(centerMeta.instant, visit.windowMin, visit.windowMax, timezone)
      : { dayStrings: [], centerIndex: 0 };

  $: dayStrings = range.dayStrings;
  $: centerIndex = range.centerIndex;

  $: dataMap = (() => {
    const m = {};
    if (Array.isArray(data)) {
      for (const item of data) {
        if (item?.day) {
          m[item.day] = item.totalCount ?? item.count ?? 0;
        }
      }
    }
    return m;
  })();

  function countDaysWithAnyData(startYmd, endYmd) {
    if (compareYmd(startYmd, endYmd) > 0) return 0;
    return Object.entries(dataMap).filter(
      ([day, count]) => isYmdInRange(day, startYmd, endYmd) && Number(count) > 0
    ).length;
  }

  function countSufficientDays(startYmd, endYmd, expectedReadingsPerDay, sufficientReadingFraction) {
    if (compareYmd(startYmd, endYmd) > 0) return 0;
    const threshold = Math.ceil(expectedReadingsPerDay * sufficientReadingFraction);
    return Object.entries(dataMap).filter(
      ([day, count]) => isYmdInRange(day, startYmd, endYmd) && Number(count) >= threshold
    ).length;
  }

  function firstCgmDayInRange(startYmd, endYmd) {
    const days = Object.entries(dataMap)
      .filter(([day, count]) => isYmdInRange(day, startYmd, endYmd) && Number(count) > 0)
      .map(([day]) => day)
      .sort();
    return days[0] || null;
  }

  function formatAlertDateRange(alert) {
    if (alert.pending) return alert.dateLabel;
    if (alert.start && alert.end && alert.start !== alert.end) return `${alert.start} to ${alert.end}`;
    return alert.date || alert.start || '';
  }

  function buildParticipantAlertSchedule(alertWindow, config, todayYmd) {
    if (!alertWindow) return [];

    const cfg = PARTICIPANT_ALERT_DEFAULTS;
    const rule2WeekOffsetDays = numericParam(config, 'rule2WeekOffsetDays', cfg.rule2WeekOffsetDays);
    const rule3MinSufficientDays = numericParam(config, 'rule3MinSufficientDays', cfg.rule3MinSufficientDays);
    const rule4MinSufficientDays = numericParam(config, 'rule4MinSufficientDays', cfg.rule4MinSufficientDays);
    const rule4StartDaysBeforePlanned = numericParam(
      config,
      'rule4StartDaysBeforePlanned',
      cfg.rule4StartDaysBeforePlanned
    );
    const sensorLifetimeReminderDays = numericParam(
      config,
      'sensorLifetimeReminderDays',
      cfg.sensorLifetimeReminderDays
    );
    const expectedReadingsPerDay = numericParam(
      config,
      'expectedReadingsPerDay',
      cfg.expectedReadingsPerDay
    );
    const sufficientReadingFraction = numericParam(
      config,
      'sufficientReadingFraction',
      cfg.sufficientReadingFraction
    );
    const sufficientDayMaxGapHours = numericParam(
      config,
      'sufficientDayMaxGapHours',
      cfg.sufficientDayMaxGapHours
    );
    const { S, P, E } = alertWindow;
    const todayThroughEnd = compareYmd(todayYmd, S) >= 0 ? todayYmd : localCalendarPlusDays(S, -1, timezone);
    const anyDataDays = countDaysWithAnyData(S, todayThroughEnd);
    const sufficientDays = countSufficientDays(
      S,
      todayThroughEnd,
      expectedReadingsPerDay,
      sufficientReadingFraction
    );
    const threshold = Math.ceil(expectedReadingsPerDay * sufficientReadingFraction);
    const rule2Day = localCalendarPlusDays(S, rule2WeekOffsetDays, timezone);
    const rule3Start = localCalendarPlusDays(P, -14, timezone);
    const rule3End = localCalendarPlusDays(P, -7, timezone);
    const rule4Start = localCalendarPlusDays(P, -rule4StartDaysBeforePlanned, timezone);
    const rule5SearchStart = localCalendarPlusDays(S, -RULE5_FIRST_CGM_LOOKBACK_DAYS_BEFORE_S, timezone);
    const firstCgmDay = firstCgmDayInRange(rule5SearchStart, E);
    const usableDef =
      Number.isFinite(sufficientDayMaxGapHours) && sufficientDayMaxGapHours > 0
        ? `usable = ≥${threshold} readings/day, no gap > ${sufficientDayMaxGapHours}h`
        : `usable = ≥${threshold} readings/day`;
    const alerts = [
      {
        id: 'participant-rule1',
        group: 'Participant alerts',
        label: 'Participant Rule 1',
        markerLabel: 'P1',
        jobId: PARTICIPANT_CGM_ALERT_JOB_ID,
        date: S,
        condition: `Sent on ${S} (window start) if no CGM readings exist on or before this day.`,
        markerType: 'line',
      },
      {
        id: 'participant-rule2',
        group: 'Participant alerts',
        label: 'Participant Rule 2',
        markerLabel: 'P2',
        jobId: PARTICIPANT_CGM_ALERT_JOB_ID,
        date: rule2Day,
        condition: `Sent on ${rule2Day} (${rule2WeekOffsetDays} days after start) if no CGM readings on or before this day.`,
        markerType: 'line',
      },
    ];

    if (compareYmd(rule3Start, rule3End) <= 0) {
      alerts.push({
        id: 'participant-rule3',
        group: 'Participant alerts',
        label: 'Participant Rule 3',
        markerLabel: 'P3',
        jobId: PARTICIPANT_CGM_ALERT_JOB_ID,
        start: rule3Start,
        end: rule3End,
        condition: `Sent any day between ${rule3Start} and ${rule3End} if fewer than ${rule3MinSufficientDays} usable CGM days have been collected since ${S}. Currently ${sufficientDays} days (${usableDef}).`,
        markerType: 'range',
      });
    }

    alerts.push({
      id: 'participant-rule4',
      group: 'Participant alerts',
      label: 'Participant Rule 4',
      markerLabel: 'P4',
      jobId: PARTICIPANT_CGM_ALERT_JOB_ID,
      start: rule4Start,
      end: E,
      condition: `Sent any day between ${rule4Start} and ${E} if fewer than ${rule4MinSufficientDays} usable CGM days have been collected since ${S}. Currently ${sufficientDays} days (${usableDef}).`,
      markerType: 'range',
    });

    if (firstCgmDay) {
      const rule5Day = localCalendarPlusDays(firstCgmDay, sensorLifetimeReminderDays, timezone);
      alerts.push({
        id: 'participant-rule5',
        group: 'Participant alerts',
        label: 'Participant Rule 5',
        markerLabel: 'P5',
        jobId: PARTICIPANT_CGM_ALERT_JOB_ID,
        date: rule5Day,
        condition: `Sensor replacement reminder, sent ${sensorLifetimeReminderDays} days after the first CGM reading from ${rule5SearchStart} through ${E} (${firstCgmDay} → ${rule5Day}).`,
        markerType: 'line',
      });
    } else {
      alerts.push({
        id: 'participant-rule5',
        group: 'Participant alerts',
        label: 'Participant Rule 5',
        jobId: PARTICIPANT_CGM_ALERT_JOB_ID,
        pending: true,
        dateLabel: `Pending — first CGM + ${sensorLifetimeReminderDays} days`,
        condition: `Sensor replacement reminder. Will be sent ${sensorLifetimeReminderDays} days after the first CGM reading; no readings yet from ${rule5SearchStart} through ${E}.`,
      });
    }

    return alerts;
  }

  function buildSiteAlertSchedule(alertWindow, config, todayYmd) {
    if (!alertWindow) return [];

    const cfg = SITE_ALERT_DEFAULTS;
    const rule2WeekOffsetDays = numericParam(config, 'rule2WeekOffsetDays', cfg.rule2WeekOffsetDays);
    const minimumUsableDaysForAlert = numericParam(
      config,
      'minimumUsableDaysForAlert',
      cfg.minimumUsableDaysForAlert
    );
    const requiredAnyDataDays = numericParam(config, 'requiredAnyDataDays', cfg.requiredAnyDataDays);
    const expectedReadingsPerDay = numericParam(
      config,
      'expectedReadingsPerDay',
      cfg.expectedReadingsPerDay
    );
    const sufficientReadingFraction = numericParam(
      config,
      'sufficientReadingFraction',
      cfg.sufficientReadingFraction
    );
    const sufficientDayMaxGapHours = numericParam(
      config,
      'sufficientDayMaxGapHours',
      cfg.sufficientDayMaxGapHours
    );
    const { S, P } = alertWindow;
    const todayThroughEnd = compareYmd(todayYmd, S) >= 0 ? todayYmd : localCalendarPlusDays(S, -1, timezone);
    const anyDataDays = countDaysWithAnyData(S, todayThroughEnd);
    const usableDays = countSufficientDays(
      S,
      todayThroughEnd,
      expectedReadingsPerDay,
      sufficientReadingFraction
    );
    const threshold = Math.ceil(expectedReadingsPerDay * sufficientReadingFraction);
    const rule2Day = localCalendarPlusDays(P, rule2WeekOffsetDays, timezone);
    const usableDef =
      Number.isFinite(sufficientDayMaxGapHours) && sufficientDayMaxGapHours > 0
        ? `usable = ≥${threshold} readings/day, no gap > ${sufficientDayMaxGapHours}h`
        : `usable = ≥${threshold} readings/day`;
    const baseCondition = `fewer than ${requiredAnyDataDays} days with any CGM data AND fewer than ${minimumUsableDaysForAlert} usable days. Currently ${anyDataDays} of ${requiredAnyDataDays} with data, ${usableDays} of ${minimumUsableDaysForAlert} usable (${usableDef}).`;

    return [
      {
        id: 'site-rule1',
        group: 'Site alerts',
        label: 'Site Rule 1',
        markerLabel: 'S1',
        jobId: SITE_CGM_ALERT_JOB_ID,
        date: P,
        condition: `Sent on ${P} (planned visit day) if ${baseCondition}`,
        markerType: 'line',
      },
      {
        id: 'site-rule2',
        group: 'Site alerts',
        label: 'Site Rule 2',
        markerLabel: 'S2',
        jobId: SITE_CGM_ALERT_JOB_ID,
        date: rule2Day,
        condition: `Sent on ${rule2Day} (${rule2WeekOffsetDays} days after planned) if ${baseCondition}`,
        markerType: 'line',
      },
    ];
  }

  $: stats = (() => {
    if (!dayStrings.length) {
      return { beforeDays: 0, afterDays: 0, beforeSum: 0, visitDaySum: 0, afterSum: 0 };
    }
    let beforeSum = 0;
    let afterSum = 0;
    const vs = dataMap[dayStrings[centerIndex]] ?? 0;
    for (let i = 0; i < dayStrings.length; i++) {
      const v = dataMap[dayStrings[i]] ?? 0;
      if (i < centerIndex) beforeSum += v;
      else if (i > centerIndex) afterSum += v;
    }
    return {
      beforeDays: centerIndex,
      afterDays: dayStrings.length - centerIndex - 1,
      beforeSum,
      visitDaySum: vs,
      afterSum,
    };
  })();

  $: visitStatusCode = String(visit?.statusCode || '');
  $: alertsEnabledForVisit = visit?.enableAlerts === true;
  $: alertsStartedForVisit = !visitStatusCode || visitStatusCode === 'START';
  $: alertsExpectedForVisit = alertsEnabledForVisit && alertsStartedForVisit;
  $: plannedAlertWindow = getPlannedAlertWindow(visit, timezone);
  $: todayYmd = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  $: participantAlertSchedule = plannedAlertWindow
    ? (dataMap, buildParticipantAlertSchedule(plannedAlertWindow, participantAlertJobConfig, todayYmd))
    : [];
  $: siteAlertSchedule = plannedAlertWindow
    ? (dataMap, buildSiteAlertSchedule(plannedAlertWindow, siteAlertJobConfig, todayYmd))
    : [];
  $: visibleAlertMarkers = [...participantAlertSchedule, ...siteAlertSchedule].filter((alert) => {
    if (!showAlertSchedule) return false;
    if (!dayStrings.length) return false;
    if (alert.pending) return false;
    if (alert.markerType === 'range') {
      return (
        compareYmd(alert.end, dayStrings[0]) >= 0 &&
        compareYmd(alert.start, dayStrings[dayStrings.length - 1]) <= 0
      );
    }
    return dayStrings.includes(alert.date);
  });
  $: participantJobDisabled = participantAlertJobConfig?.enabled === false;
  $: siteJobDisabled = siteAlertJobConfig?.enabled === false;

  function formatDisplayDate(dateStr) {
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${mm}/${dd}`;
  }

  function labelForIndex(i) {
    const rel = i - centerIndex;
    if (rel === 0) return '0';
    return rel > 0 ? `+${rel}` : `${rel}`;
  }

  /** Below threshold: bluish; at/above: green-teal (aligned with DailyBarChart main tones). */
  function barColorsForCount(count, isDark) {
    const met = count >= targetValue;
    if (isDark) {
      return met
        ? 'rgba(13, 148, 136, 0.75)'
        : 'rgba(13, 148, 236, 0.65)';
    }
    return met ? 'rgba(13, 148, 136, 0.72)' : 'rgba(59, 130, 246, 0.58)';
  }

  function buildAlertAnnotations(yMax, isDark) {
    const annotations = {};

    for (const alert of visibleAlertMarkers) {
      const isSite = alert.group === 'Site alerts';
      const color = isSite
        ? isDark
          ? 'rgba(196, 181, 253, 0.95)'
          : 'rgba(124, 58, 237, 0.9)'
        : isDark
          ? 'rgba(251, 191, 36, 0.95)'
          : 'rgba(217, 119, 6, 0.9)';
      if (alert.markerType === 'range') {
        const boundaryMarkers = [
          { key: 'start', date: alert.start, suffix: 'start' },
          { key: 'end', date: alert.end, suffix: 'end' },
        ].filter((marker) => dayStrings.includes(marker.date));

        for (const marker of boundaryMarkers) {
          const x = dayStrings.indexOf(marker.date);
          annotations[`${alert.id}-${marker.key}`] = {
            type: 'line',
            xMin: x,
            xMax: x,
            yMin: 0,
            yMax,
            borderColor: color,
            borderWidth: 1.5,
            borderDash: [6, 4],
            label: {
              display: true,
              content: `${alert.markerLabel} ${marker.suffix}`,
              position: marker.key === 'start' ? 'start' : 'end',
              backgroundColor: isDark ? 'rgba(31, 41, 55, 0.85)' : 'rgba(255, 255, 255, 0.85)',
              color: isDark ? 'white' : 'black',
              font: { size: 9 },
            },
          };
        }
      } else {
        const x = dayStrings.indexOf(alert.date);
        annotations[alert.id] = {
          type: 'line',
          xMin: x,
          xMax: x,
          yMin: 0,
          yMax,
          borderColor: color,
          borderWidth: 1.5,
          borderDash: [4, 3],
          label: {
            display: true,
            content: alert.markerLabel,
            position: 'start',
            backgroundColor: isDark ? 'rgba(31, 41, 55, 0.85)' : 'rgba(255, 255, 255, 0.85)',
            color: isDark ? 'white' : 'black',
            font: { size: 9 },
          },
        };
      }
    }

    return annotations;
  }

  function baseOptions(isDark) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 4, right: 8, top: 4, bottom: 0 } },
      scales: {
        x: {
          ticks: {
            color: isDark ? 'white' : 'black',
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            autoSkipPadding: 4,
            maxTicksLimit: 60,
            font: { size: 9 },
          },
          grid: { display: false },
          offset: true,
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Count',
            color: isDark ? 'white' : 'black',
          },
          position: 'left',
          ticks: { color: isDark ? 'white' : 'black' },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const i = items[0]?.dataIndex;
              if (i == null || !dayStrings[i]) return '';
              return `${dayStrings[i]} (${labelForIndex(i)})`;
            },
            label: (ctx) => `Readings: ${ctx.raw ?? 0}`,
          },
        },
        annotation: {
          annotations: {
            targetLine: {
              type: 'line',
              yMin: 0,
              yMax: 0,
              borderColor: isDark ? 'rgba(110, 210, 170, 0.8)' : 'black',
              borderWidth: 0.5,
              label: {
                display: true,
                position: 'end',
                font: { size: 10 },
              },
            },
            visitDayLine: {
              type: 'line',
              xMin: 0,
              xMax: 0,
              yMin: 0,
              yMax: 0,
              borderColor: isDark ? 'rgba(248, 113, 113, 0.95)' : 'rgba(220, 38, 38, 0.9)',
              borderWidth: 2,
              label: {
                display: false,
              },
            },
          },
        },
      },
    };
  }

  function createChart() {
    if (!canvas) return;
    ensurePlugins();

    const isDark = get(theme) === 'dark';
    const options = baseOptions(isDark);

    if (!dayStrings.length) {
      if (chart) {
        chart.destroy();
        chart = undefined;
      }
      chart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: { labels: [], datasets: [{ data: [] }] },
        options,
      });
      tick().then(() => chart?.resize());
      return;
    }

    const chartData = dayStrings.map((d) => dataMap[d] ?? 0);

    const backgroundColors = chartData.map((count) => barColorsForCount(count, isDark));

    const displayLabels = dayStrings.map((d, i) => `${formatDisplayDate(d)}\n${labelForIndex(i)}`);

    const yMax = Math.max(targetValue * 1.1, ...chartData, 1);

    const tgt = options.plugins.annotation.annotations.targetLine;
    tgt.yMin = targetValue;
    tgt.yMax = targetValue;
    tgt.label.content = `Target: ${targetValue}`;
    tgt.label.backgroundColor = isDark ? 'rgba(33,33,33,0.75)' : 'rgba(0,0,0,0.65)';
    tgt.label.color = 'white';
    tgt.xMin = 0;
    tgt.xMax = dayStrings.length - 1;

    const mv = options.plugins.annotation.annotations.visitDayLine;
    mv.xMin = centerIndex;
    mv.xMax = centerIndex;
    mv.yMin = 0;
    mv.yMax = yMax;
    options.plugins.annotation.annotations = {
      ...options.plugins.annotation.annotations,
      ...buildAlertAnnotations(yMax, isDark),
    };

    if (chart) {
      chart.destroy();
    }

    chart = new Chart(canvas.getContext('2d'), {
      type: 'bar',
      data: {
        labels: displayLabels,
        datasets: [
          {
            label: 'Readings',
            data: chartData,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors,
            borderWidth: 1,
            categoryPercentage: 0.85,
            barPercentage: 1,
          },
        ],
      },
      options,
    });

    tick().then(() => {
      chart?.resize();
    });
  }

  function chartResize(node) {
    const ro = new ResizeObserver(() => {
      chart?.resize();
    });
    ro.observe(node);
    return {
      destroy() {
        ro.disconnect();
      },
    };
  }

  $: themeMode = $theme;
  $: {
    void data;
    void visit;
    void visitKey;
    void anchorMode;
    void timezone;
    void dayStrings;
    void centerIndex;
    void dataMap;
    void targetValue;
    void participantAlertJobConfig;
    void siteAlertJobConfig;
    void showAlertSchedule;
    void visibleAlertMarkers;
    void themeMode;
    if (canvas) createChart();
  }

  onDestroy(() => {
    if (chart) {
      chart.destroy();
      chart = undefined;
    }
  });
</script>

{#if visit && dayStrings.length > 0 && centerMeta.instant}
  <div class="mb-3 text-xs text-gray-700 dark:text-slate-300 space-y-1">
    <p class="font-medium text-gray-800 dark:text-slate-200">
      {visit.name || 'Visit'} —
      {#if centerMeta.usedVisitDate}
        visit date {formatToLocalDate(visit.visitDate, timezone)}
      {:else if centerMeta.usedStarted}
        started {formatToLocalDate(visit.started, timezone)}
      {:else}
        planned {formatToLocalDate(visit.planned, timezone)}
      {/if}
      (window −{visit.windowMin ?? 0} / +{visit.windowMax ?? 0} days)
      {#if centerMeta.fellBackHint === 'started'}
        <span class="font-normal text-amber-700 dark:text-amber-300"> · using planned (started not set)</span>
      {:else if centerMeta.fellBackHint === 'visitDate'}
        <span class="font-normal text-amber-700 dark:text-amber-300"> · using planned (visit date not set)</span>
      {/if}
    </p>
    <div class="flex flex-wrap gap-x-6 gap-y-1">
      <span
        >Before visit <span class="text-gray-600 dark:text-gray-400">({stats.beforeDays} days)</span>:
        <strong>{stats.beforeSum}</strong> readings</span
      >
      <span
        >Visit day: <strong>{stats.visitDaySum}</strong> readings</span
      >
      <span
        >After visit <span class="text-gray-600 dark:text-gray-400">({stats.afterDays} days)</span>:
        <strong>{stats.afterSum}</strong> readings</span
      >
    </div>
    {#if showAlertSchedule && !plannedAlertWindow}
      <p class="text-amber-700 dark:text-amber-300">
        Alert schedule is unavailable because this visit has no planned date.
      </p>
    {:else if showAlertSchedule && plannedAlertWindow}
      {#if !alertsExpectedForVisit}
        <p class="text-amber-700 dark:text-amber-300">
          Alerts are not expected for this visit
          {#if !alertsEnabledForVisit}
            because Enable Alerts is off.
          {:else if !alertsStartedForVisit}
            because status is {visitStatusCode || 'not set'}, not START.
          {/if}
          The theoretical schedule is shown below.
        </p>
      {/if}
      <div class="grid gap-2 lg:grid-cols-2">
        <div class="rounded border border-stone-200 dark:border-gray-700 p-2">
          <p class="font-medium text-gray-800 dark:text-slate-200">Participant alerts</p>
          <ul class="mt-1 space-y-1">
            {#each participantAlertSchedule as alert (alert.id)}
              <li>
                <span class="font-medium">{formatAlertDateRange(alert)}</span>
                <span class="text-gray-600 dark:text-gray-400"> · {alert.label}</span>
                <div class="text-gray-600 dark:text-gray-400">{alert.condition}</div>
                <div class="text-[10px] text-gray-500 dark:text-gray-500">
                  {alert.jobId}{participantJobDisabled ? ' · job config disabled' : ''}
                </div>
              </li>
            {/each}
          </ul>
        </div>
        <div class="rounded border border-stone-200 dark:border-gray-700 p-2">
          <p class="font-medium text-gray-800 dark:text-slate-200">Site alerts</p>
          <ul class="mt-1 space-y-1">
            {#each siteAlertSchedule as alert (alert.id)}
              <li>
                <span class="font-medium">{formatAlertDateRange(alert)}</span>
                <span class="text-gray-600 dark:text-gray-400"> · {alert.label}</span>
                <div class="text-gray-600 dark:text-gray-400">{alert.condition}</div>
                <div class="text-[10px] text-gray-500 dark:text-gray-500">
                  {alert.jobId}{siteJobDisabled ? ' · job config disabled' : ''}
                </div>
              </li>
            {/each}
          </ul>
        </div>
      </div>
    {/if}
  </div>
{:else if visit && !centerMeta.instant}
  <p class="text-sm text-amber-700 dark:text-amber-300 mb-2">
    This visit has no date to center the window
    {#if anchorMode === 'planned'}
      (planned is missing).
    {:else if anchorMode === 'started'}
      (started and planned are missing).
    {:else if anchorMode === 'visitDate'}
      (visit date and planned are missing).
    {/if}
  </p>
{/if}

<div
  class="visit-chart-host relative w-full min-w-0 h-[260px]"
  use:chartResize
>
  <canvas bind:this={canvas} class="block w-full h-full max-w-full"></canvas>
</div>

<style>
  .visit-chart-host :global(canvas) {
    max-width: 100%;
  }
</style>
